import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { FormRef } from '@lib/components/ReactHookForm/types';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Form from '@lib/components/ReactHookForm/FormContainer';
import useSearchListQueries from 'routes/Sectors/Sector/utils/useSearchListQueries';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import getDefaultValuesFromQueryData from 'routes/Sectors/Sector/utils/getDefaultValuesFromQueryData';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import { materialSymbolsOutlined } from '@lib/enums/common';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import { APP_URLS } from 'constants/urls';
import { SectorViewMode } from 'routes/Sectors/Sector/enums';
import getSectorFormFields, {
  defaultValues,
} from './utils/getSectorFormFields';
import useActions from './utils/useActions';

type Props = {
  mode: SectorViewMode;
};

const modals = [Modals.DestroyModal];

export default function SectorFormContainer({ mode }: Props) {
  const isEdit = mode === SectorViewMode.Edit;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const { pathname, search } = useLocation();
  const formRef = useRef<FormRef>(undefined);
  const useModalManagementResult = useModalManagement(modals);
  const {
    getSitesLoadOptions,
    getUsersLoadOptions,
    getResponsiblePersonLoadOptions,
  } = useSearchListQueries();
  const useActionsResult = useActions({
    id,
    mode,
    useModalManagementResult,
    pathname,
    search,
  });
  const {
    graphQLErrors,
    loading,
    onCancel,
    onSubmitForm,
    onViewHandler,
    sectorQueryHookResult,
    sitesQueryHookResult,
    usersQueryHookResult,
  } = useActionsResult;
  const { allIds: assigneeIds = [] } = usersQueryHookResult;
  const { allIds: sitesIds = [] } = sitesQueryHookResult;

  const { data, error, firstLoading } = sectorQueryHookResult;
  const readOnly = _get(data, 'readOnly', false);

  const fields = getSectorFormFields({
    assigneeIds,
    getSitesLoadOptions,
    getUsersLoadOptions,
    getResponsiblePersonLoadOptions,
    sectorId: data?.id,
    sitesIds,
    t,
  });

  useEffect(() => {
    if (mode === SectorViewMode.Edit && readOnly) {
      navigate(
        APP_URLS.dashboard.sectors.view.getDynamicPath({ pathParts: { id } }),
      );
    }
  }, [id, mode, navigate, readOnly]);

  if (error) {
    return (
      <ErrorPage
        status={getStatusFromError(error)}
        description={getDescriptionFromError(error)}
      />
    );
  }

  const defaultFormValues =
    getDefaultValuesFromQueryData(data, assigneeIds, sitesIds) || defaultValues;

  const actionsComponent = () => (
    <ActionsRow
      actions={[
        <Button
          buttonType={ButtonTypes.secondaryOutlined}
          buttonText={t('cancel')}
          data-test-id="sector-form__cancel-button"
          leftIcon="close"
          onClick={onCancel}
          fullWidth
          disabled={loading}
        />,
        <Button
          type="submit"
          buttonType={ButtonTypes.primaryFilled}
          buttonText={isEdit ? t('save-changes') : t('create')}
          data-test-id="sector-form__save-button"
          leftIcon="check"
          fullWidth
          disabled={loading}
        />,
      ]}
    />
  );

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({
          pathname,
          t,
          entity: data || undefined,
        })}
      />
      {firstLoading ? (
        <CircleLoader />
      ) : (
        <DashboardContentMaxWidth>
          <PageBody withoutHeader>
            <DashboardTitleHeader
              title={isEdit ? t('edit-sector') : t('create-sector')}
              actions={
                isEdit ? (
                  <Button
                    buttonText={t('view-sector')}
                    buttonType={ButtonTypes.primaryOutlined}
                    onClick={onViewHandler}
                    leftIcon="edit"
                    leftIconClassName={materialSymbolsOutlined}
                  />
                ) : undefined
              }
            />
            <WhiteBox>
              <Form
                actionsComponent={actionsComponent}
                formId="sector"
                defaultValues={defaultFormValues}
                fields={fields}
                formRef={formRef}
                graphQLErrors={graphQLErrors}
                loading={loading}
                onSubmit={onSubmitForm}
              />
            </WhiteBox>
          </PageBody>
        </DashboardContentMaxWidth>
      )}
    </>
  );
}
